import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit6Notes: UnitNotes = {
  unitId: "unit-6",
  unitName: "Unit 6: Energy and Momentum of Rotating Systems",
  examWeight: "5–8%",
  focus: "This unit bridges the gap between linear mechanics and rotational dynamics, applying conservation of energy and momentum to rotating or rolling systems.",
  sections: [
    {
      id: "topic-6-1",
      title: "Topic 6.1: Rotational Kinetic Energy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Conceptual Definition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Just as an object has Translational Kinetic Energy (<InlineMath math="K_{trans}" />) due to the linear motion of its center of mass, a rigid body has <strong>Rotational Kinetic Energy (<InlineMath math="K_{rot}" />)</strong> due to its rotation about an axis.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-bold text-slate-900 mb-2">Analogy:</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li>Mass (<InlineMath math="m" />) is to linear kinetic energy as <strong>Rotational Inertia (<InlineMath math="I" />)</strong> is to rotational kinetic energy.</li>
                <li>Velocity (<InlineMath math="v" />) is to linear KE as <strong>Angular Velocity (<InlineMath math="\omega" />)</strong> is to rotational KE.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. The Formula</h3>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 text-center">
              <BlockMath math="K_{rot} = \frac{1}{2}I\omega^2" />
              <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm text-slate-600">
                <p><strong>I:</strong> Rotational Inertia (<InlineMath math="kg\cdot m^2" />)</p>
                <p><strong>ω:</strong> Angular Velocity (<InlineMath math="rad/s" />)</p>
                <p><strong>Unit:</strong> Joules (J)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Total Kinetic Energy (Rolling Objects)</h3>
            <p className="text-slate-700 mb-4">For an object translating AND rotating (e.g., a rolling bowling ball):</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="K_{total} = K_{trans} + K_{rot} = \frac{1}{2}mv^2 + \frac{1}{2}I\omega^2" />
            </div>
            <ImportantNote>
              <strong>Crucial Condition:</strong> If the object rolls <strong>without slipping</strong>, linear velocity and angular velocity are linked by <InlineMath math="v = r\omega" />.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Energy Distribution</h3>
            <p className="text-slate-700 leading-relaxed">
              When an object rolls down a ramp from rest, initial <InlineMath math="U_g" /> converts into both <InlineMath math="K_{trans}" /> and <InlineMath math="K_{rot}" />.
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2 text-slate-700">
              <li>Objects with a <strong>larger Rotational Inertia (I)</strong> (like a hoop) "steal" more energy for rotation.</li>
              <li><strong>Result:</strong> Less energy is available for translation (<InlineMath math="\frac{1}{2}mv^2" />), meaning the object moves <strong>slower</strong> linearly.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-2",
      title: "Topic 6.2: Torque and Work",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Work-Energy Theorem for Rotation</h3>
            <p className="text-slate-700 mb-4">In rotational motion, a <strong>Net Torque (<InlineMath math="\tau" />)</strong> applied over an <strong>Angular Displacement (<InlineMath math="\Delta\theta" />)</strong> does work.</p>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
              <BlockMath math="W = \tau_{net}\Delta\theta" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Rotational Work-Energy Theorem</h3>
            <p className="text-slate-700 mb-2">Net work done by torque results in a change in rotational kinetic energy:</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="W_{net} = \Delta K_{rot} = \frac{1}{2}I\omega_f^2 - \frac{1}{2}I\omega_i^2" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Rotational Power</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <BlockMath math="P = \frac{W}{t} = \tau \left(\frac{\Delta\theta}{t}\right) = \tau\omega" />
              </div>
              <div className="flex items-center">
                <p className="text-sm text-slate-600 italic"><strong>Analogy:</strong> Similar to linear power <InlineMath math="P = Fv" />.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Graphical Analysis</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <p className="text-slate-800"><strong>Graph:</strong> Torque (<InlineMath math="\tau" />) vs. Angular Displacement (<InlineMath math="\theta" />)</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Area:</strong> The area under the curve represents the <strong>Work Done</strong> on the system.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-3",
      title: "Topic 6.3: Angular Momentum and Angular Impulse",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Angular Momentum (L)</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">A. For a Rigid Rotating Object</h4>
                <BlockMath math="L = I\omega" />
                <p className="text-xs text-slate-600 mt-1">Units: <InlineMath math="kg\cdot m^2/s" />. Used for solids spinning about an axis.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">B. For a Point Particle Moving in a Straight Line</h4>
                <BlockMath math="L = mvr_{\perp} \quad \text{or} \quad L = mvr\sin\theta" />
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                  <li><strong>r:</strong> Distance from pivot to particle.</li>
                  <li><strong>r⊥:</strong> Lever arm distance ("closest approach").</li>
                  <li><strong>θ:</strong> Angle between velocity and position vectors.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Angular Impulse (ΔL)</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <BlockMath math="\text{Angular Impulse} = \tau_{net}\Delta t = \Delta L = I\omega_f - I\omega_i" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Graphical Analysis</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <p className="text-slate-800"><strong>Graph:</strong> Torque (<InlineMath math="\tau" />) vs. Time (<InlineMath math="t" />)</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Area:</strong> The area under the curve represents <strong>Angular Impulse</strong> (Change in Angular Momentum, <InlineMath math="\Delta L" />).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-4",
      title: "Topic 6.4: Conservation of Angular Momentum",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. The Law of Conservation</h3>
            <p className="text-slate-700 mb-4">If the <strong>net external torque</strong> on a system is zero, the total angular momentum remains constant:</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
              <BlockMath math="L_i = L_f \Rightarrow \Sigma (I\omega)_i = \Sigma (I\omega)_f" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Common Scenarios</h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">A. The "Ice Skater" Effect (Changing Shape)</h4>
                <p className="text-sm text-slate-700 mt-1">Arms pulled in → mass closer to axis → <InlineMath math="I" /> decreases → <InlineMath math="\omega" /> must increase. <strong>Note:</strong> <InlineMath math="K_{rot}" /> increases because the skater does work.</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">B. Rotational Collisions</h4>
                <p className="text-sm text-slate-700 mt-1">Clay hitting a rod: <InlineMath math="mvr = (I_{rod} + I_{particle})\omega_f" />. Remember <InlineMath math="I_{particle} = mr^2" />.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">C. Planetary Orbits</h4>
                <p className="text-sm text-slate-700 mt-1">Gravity acts toward the sun (pivot). Since force is parallel to radius, torque is <strong>zero</strong>. This explains Kepler's 2nd Law: planets move faster when closer to the star.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-5",
      title: "Topic 6.5: Rolling",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Rolling Without Slipping</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <KeyTerm term="Constraint 1" definition="Linear velocity v_cm = Rω" />
              <KeyTerm term="Constraint 2" definition="Linear acceleration a_cm = Rα" />
            </div>
            <p className="text-sm text-slate-700 bg-slate-100 p-3 rounded border border-slate-200">
              <strong>Friction:</strong> Static friction provides torque but does <strong>NO work</strong> (point of contact is momentarily at rest). Mechanical energy is conserved.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Rolling With Slipping</h3>
            <p className="text-slate-700 leading-relaxed">Occurs when <InlineMath math="R\omega \neq v" />. Kinetic friction acts on the object, and mechanical energy is <strong>NOT</strong> conserved (lost to heat).</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. The Great Race (Incline Problem)</h3>
            <p className="text-slate-700 mb-4">Ranking finish order for same mass <InlineMath math="M" /> and radius <InlineMath math="R" />:</p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="font-bold text-green-700">1st</span>
                <p className="text-sm"><strong>Box (No friction):</strong> No energy for rotation; all becomes translational <InlineMath math="K" />.</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <span className="font-bold text-yellow-700">2nd</span>
                <p className="text-sm"><strong>Solid Sphere:</strong> Lowest <InlineMath math="I" /> (<InlineMath math="\frac{2}{5}MR^2" />). Takes least energy to rotate.</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <span className="font-bold text-orange-700">3rd</span>
                <p className="text-sm"><strong>Solid Disk:</strong> Medium <InlineMath math="I" /> (<InlineMath math="\frac{1}{2}MR^2" />).</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="font-bold text-red-700">4th</span>
                <p className="text-sm"><strong>Hoop:</strong> Highest <InlineMath math="I" /> (<InlineMath math="MR^2" />). Hardest to rotate; "steals" most energy.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-6",
      title: "Topic 6.6: Motion of Orbiting Satellites",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Forces and Velocity</h3>
            <p className="text-slate-700 mb-4">Gravity provides the centripetal force (<InlineMath math="F_g = F_c" />):</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-center">
              <BlockMath math="v_{orbit} = \sqrt{\frac{GM}{r}}" />
              <p className="text-xs text-slate-500 mt-2">M = mass of central body; r = distance from center to center.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Universal Potential Energy</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <BlockMath math="U_g = -\frac{GMm}{r}" />
              <p className="text-xs text-slate-700 mt-2"><strong>Negative Sign:</strong> Indicates a "bound" system. As <InlineMath math="r" /> increases, <InlineMath math="U_g" /> increases toward zero.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Total Mechanical Energy in Orbit</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="E_{total} = K + U_g = -\frac{GMm}{2r}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Escape Velocity</h3>
            <p className="text-slate-700 mb-2">Energy required to reach distance <InlineMath math="\infty" /> with velocity 0:</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="v_{esc} = \sqrt{\frac{2GM}{r}}" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "summary-analogies",
      title: "Unit Summary: Key Analogies",
      content: (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 border border-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Linear Concept</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Var</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Rotational Concept</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Var</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Relation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-sm text-slate-700">
                <tr><td className="px-4 py-2">Position</td><td className="px-4 py-2">x</td><td className="px-4 py-2">Angular Position</td><td className="px-4 py-2">θ</td><td className="px-4 py-2">x = rθ</td></tr>
                <tr><td className="px-4 py-2">Velocity</td><td className="px-4 py-2">v</td><td className="px-4 py-2">Angular Velocity</td><td className="px-4 py-2">ω</td><td className="px-4 py-2">v = rω</td></tr>
                <tr><td className="px-4 py-2">Acceleration</td><td className="px-4 py-2">a</td><td className="px-4 py-2">Angular Acceleration</td><td className="px-4 py-2">α</td><td className="px-4 py-2">a = rα</td></tr>
                <tr><td className="px-4 py-2">Mass (Inertia)</td><td className="px-4 py-2">m</td><td className="px-4 py-2">Rotational Inertia</td><td className="px-4 py-2">I</td><td className="px-4 py-2">Σmr²</td></tr>
                <tr><td className="px-4 py-2">Force</td><td className="px-4 py-2">F</td><td className="px-4 py-2">Torque</td><td className="px-4 py-2">τ</td><td className="px-4 py-2">τ = rFsinθ</td></tr>
                <tr><td className="px-4 py-2">Momentum</td><td className="px-4 py-2">p</td><td className="px-4 py-2">Angular Momentum</td><td className="px-4 py-2">L</td><td className="px-4 py-2">L = Iω</td></tr>
                <tr><td className="px-4 py-2">Kinetic Energy</td><td className="px-4 py-2">½mv²</td><td className="px-4 py-2">Rotational KE</td><td className="px-4 py-2">½Iω²</td><td className="px-4 py-2">N/A</td></tr>
              </tbody>
            </table>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2">Important AP Exam Tips</h3>
            <ul className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Pivot Point Selection:</strong> For collisions, always calculate angular momentum relative to the hinge/pivot point.</li>
              <li><strong>Translation vs. Rotation:</strong> Always check if both terms (<InlineMath math="\frac{1}{2}mv^2 + \frac{1}{2}I\omega^2" />) are required.</li>
              <li><strong>Justification:</strong> Energy arguments are usually faster and less prone to error than Torque arguments in FRQs.</li>
              <li><strong>Satellite Mass:</strong> Orbital speed depends on the mass of the <em>planet</em>, not the satellite. A heavy and light satellite orbit at the same speed at the same radius.</li>
            </ul>
          </div>
        </div>
      )
    }
  ]
};

export default physicsUnit6Notes;